import { isAgendaExcludedCase, isWaitingCase } from "./caseLifecycle.ts";
import { nextStepLabel } from "./format.ts";
import {
  completeListingFollowUp,
  LISTING_WORKFLOW_RULES,
  listingActionId,
  recoverListingNextAction
} from "./listingWorkflow.ts";
import { followUpActivityType } from "./shotScore.ts";
import { isListingPreparationCase } from "./listingPreparation.ts";
import { isReservationControl, reservationControlFollowUp } from "./reservationWorkflow.ts";
import type { FollowUp, RealEstateCase, TimelineActivity } from "./types";

export type NextActionBucket = "Omeškané" | "Dnes" | "Nadchádzajúce" | "Bez kroku" | "Neaktívne";

const createId = (prefix: string) => {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return `${prefix}-${crypto.randomUUID()}`;
  return `${prefix}-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const createActivity = (
  activity: Omit<TimelineActivity, "id" | "createdAt">,
  createdAt: string
): TimelineActivity => ({ id: createId("activity"), createdAt, ...activity });

export const followUpTimestamp = (followUp: FollowUp | undefined) => {
  if (!followUp?.date || !followUp.time) return Number.NaN;
  return new Date(`${followUp.date}T${followUp.time}:00`).getTime();
};

export const nextActionBucket = (caseItem: RealEstateCase, now: Date): NextActionBucket => {
  if (isAgendaExcludedCase(caseItem)) return "Neaktívne";
  if (isListingPreparationCase(caseItem)) return "Neaktívne";
  if (!caseItem.followUp?.date || !caseItem.followUp.time || !caseItem.followUp.purpose) return "Bez kroku";

  const timestamp = followUpTimestamp(caseItem.followUp);
  if (Number.isFinite(timestamp) && timestamp < now.getTime()) return "Omeškané";

  const today = [now.getFullYear(), String(now.getMonth() + 1).padStart(2, "0"), String(now.getDate()).padStart(2, "0")].join("-");
  if (caseItem.followUp.date === today) return "Dnes";
  return isWaitingCase(caseItem) ? "Neaktívne" : "Nadchádzajúce";
};

export const compareNextActions = (first: RealEstateCase, second: RealEstateCase, now: Date) => {
  const order: Record<NextActionBucket, number> = {
    Omeškané: 0,
    Dnes: 1,
    "Bez kroku": 2,
    Nadchádzajúce: 3,
    Neaktívne: 4
  };
  const bucketDifference = order[nextActionBucket(first, now)] - order[nextActionBucket(second, now)];
  if (bucketDifference !== 0) return bucketDifference;
  if ((first.priority === "HOT") !== (second.priority === "HOT")) return first.priority === "HOT" ? -1 : 1;
  return `${first.followUp?.date ?? "9999-99-99"}${first.followUp?.time ?? "99:99"}`.localeCompare(
    `${second.followUp?.date ?? "9999-99-99"}${second.followUp?.time ?? "99:99"}`
  );
};

export const overdueTimeLabel = (followUp: FollowUp | undefined, now: Date) => {
  const timestamp = followUpTimestamp(followUp);
  if (!Number.isFinite(timestamp) || timestamp >= now.getTime()) return "";
  const hours = Math.max(1, Math.floor((now.getTime() - timestamp) / 3_600_000));
  if (hours < 24) return hours === 1 ? "mešká 1 hodinu" : `mešká ${hours} hodín`;
  const days = Math.floor(hours / 24);
  if (days === 1) return "mešká 1 deň";
  if (days < 5) return `mešká ${days} dni`;
  return `mešká ${days} dní`;
};

export const nextActionCompletionLabel = (caseItem: RealEstateCase) => {
  if (isReservationControl(caseItem)) return "Vyhodnotiť rezerváciu";
  const listingAction = listingActionId(caseItem);
  if (listingAction) return LISTING_WORKFLOW_RULES[listingAction].completionLabel;

  const context = `${caseItem.followUp?.purpose ?? ""} ${caseItem.stage}`.toLocaleLowerCase("sk");
  if (context.includes("obhliad")) return "Obhliadka prebehla";
  if (context.includes("stretnut")) return "Stretnutie prebehlo";
  if (context.includes("follow-up") || context.includes("follow up")) return "Follow-up vybavený";
  if (context.includes("vola") || context.includes("kontakt")) return "Klient kontaktovaný";
  return "Hotovo";
};

export const nextManualStepDraft = (now = new Date()) => {
  const next = new Date(now);
  next.setMinutes(0, 0, 0);
  next.setHours(next.getHours() + 1);
  const date = [next.getFullYear(), String(next.getMonth() + 1).padStart(2, "0"), String(next.getDate()).padStart(2, "0")].join("-");
  return { date, time: `${String(next.getHours()).padStart(2, "0")}:00` };
};

export const completeCaseNextAction = (caseItem: RealEstateCase, completedAt: string) => {
  if (!caseItem.followUp) return null;
  const completedFollowUp = caseItem.followUp;
  const listingCompletion = completeListingFollowUp(caseItem, completedAt);
  const completionTitle = listingCompletion?.completionTitle ?? `${nextActionCompletionLabel(caseItem)}`;
  const completionActivity = createActivity({
    type: "follow-up",
    activityKind: followUpActivityType(caseItem),
    title: completionTitle,
    body: `Dokončené: ${nextStepLabel(completedFollowUp)}.`
  }, completedAt);
  const scheduledActivity = listingCompletion?.followUp
    ? createActivity({
        type: "system",
        title: "Ďalší krok automaticky naplánovaný",
        body: nextStepLabel(listingCompletion.followUp),
        followUp: listingCompletion.followUp
      }, completedAt)
    : undefined;
  const followUp = listingCompletion?.followUp;

  return {
    caseItem: {
      ...caseItem,
      followUp,
      listingFollowUpSequence: listingCompletion?.sequence ?? caseItem.listingFollowUpSequence,
      nextStep: followUp ? nextStepLabel(followUp) : "Chýba ďalší krok",
      timeline: [completionActivity, scheduledActivity, ...caseItem.timeline].filter(
        (activity): activity is TimelineActivity => Boolean(activity)
      ),
      updatedAt: completedAt
    },
    activityId: completionActivity.id,
    activityType: completionActivity.activityKind,
    completedAt
  };
};

export const completeCaseNextActionWithManualNext = (
  caseItem: RealEstateCase,
  nextFollowUp: Pick<FollowUp, "date" | "time" | "endTime" | "purpose">,
  completedAt: string
) => {
  const completion = completeCaseNextAction(caseItem, completedAt);
  if (!completion || completion.caseItem.followUp) return completion;

  const followUp: FollowUp = {
    date: nextFollowUp.date,
    time: nextFollowUp.time,
    ...(nextFollowUp.endTime ? { endTime: nextFollowUp.endTime } : {}),
    purpose: nextFollowUp.purpose.trim()
  };
  if (!followUp.date || !followUp.time || !followUp.purpose) return null;

  const scheduledActivity = createActivity({
    type: "system",
    title: "Ďalší krok naplánovaný",
    body: nextStepLabel(followUp),
    followUp
  }, completedAt);

  return {
    ...completion,
    caseItem: {
      ...completion.caseItem,
      followUp,
      nextStep: nextStepLabel(followUp),
      timeline: [completion.caseItem.timeline[0], scheduledActivity, ...completion.caseItem.timeline.slice(1)],
      updatedAt: completedAt
    }
  };
};

export const recoverDeterministicNextAction = (caseItem: RealEstateCase, recoveredAt = new Date().toISOString()) => {
  if (caseItem.stage === "Rezervované" && caseItem.reservationUntil && !caseItem.followUp) {
    const followUp = reservationControlFollowUp(caseItem.reservationUntil);
    return {
      ...caseItem,
      followUp,
      nextStep: nextStepLabel(followUp),
      updatedAt: recoveredAt
    };
  }
  const followUp = recoverListingNextAction(caseItem);
  if (!followUp) return caseItem;

  const sequence = caseItem.listingFollowUpSequence;
  const recoveredSequence = sequence?.status === "Dokončená"
    ? { ...sequence, status: "Aktívna" as const, currentStep: 3 as const }
    : sequence;
  const activity = createActivity({
    type: "system",
    title: "Ďalší krok obnovený z workflow",
    body: `Podľa histórie prípadu bolo obnovené: ${nextStepLabel(followUp)}.`,
    followUp
  }, recoveredAt);

  return {
    ...caseItem,
    followUp,
    listingFollowUpSequence: recoveredSequence,
    nextStep: nextStepLabel(followUp),
    timeline: [activity, ...caseItem.timeline],
    updatedAt: recoveredAt
  };
};
